import React, { useEffect, useRef, useState } from 'react'
import { Autocomplete, Box, Button, CircularProgress, Grid, TextField, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import { getBanksAc, getCurrencyAc, getLesseeAc, getLessorAc, getOwnerListApi, getStorage } from '../../../shared_elements/actionCreators';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import AInvItems from './AInvItems';
import { AttachementsUpload, ButtonLoader } from '../../../shared_elements/components';
import { regexConstants } from '../../../constants/regEx';
import { formatBytes } from '../../../utils/fileUpload';
import { globalGetParamsService, globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import { getAssetListApi, getInvoiceRateItemsApi, partyDropdownApi, getProductServiceDropdownApi, getChartOfAccountDropdownApi, getClassesDropdownApi } from '../apiService';
import { getQbStatusApi } from '../../quickBooks/apiService';
import { hanwahaInstance } from '../../../shared_elements';
import { useSnackbar } from 'notistack';
import PenaltyCalculationPreview from './PenaltyCalculationPreview';

const QB_FIELD_EXCLUDED_TYPES = ['rentalInvoice', 'reserveInvoice', 'linkedInvoice', 'reconcileInvoice'];

const AInvbasicDetail = ({ onToggleModal, modeType, type, addEditInvoice, onFieldChange, onRestErrorKey, invoice, error, onAddRemoveInvoiceItem, handleFileChange, tableErrors, resetTableError, leaseSlug, onActiveTaxLoad, lgGroupedAllocation, isGroupedLG, onAccountTypeChange, invoiceNumberLoading, onQbStatusLoad, showPenaltyCalculation }) => {
	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const [rateItems, setRateItems] = useState([]);
	const [rateItemsLoading, setRateItemsLoading] = useState(false);
	const onFieldChangeRef = useRef(onFieldChange);
	useEffect(() => { onFieldChangeRef.current = onFieldChange; });
	const { lessee, lessor, banks, currencies, storageDetails, owner } = useSelector((state) => state.leases);
	const [cleared, setCleared] = useState(false);
	const [isBankEditable, setIsBankEditable] = useState(!invoice?.bank);
	const [fromLinkedWithAsset, setFromLinkedWithAsset] = useState(false);
	const [toLinkedWithAsset, setToLinkedWithAsset] = useState(false);
	const isZeroPenalty = !Number(invoice?.calculation_details_preview?.penalty_amount);
	const [dropDownloader, setDropDownLoader] = useState({ asset: false, lessee: false, lessor: false, banks: false, currencies: false, owner: false, storage: false });
	const [assetList, setAssetList] = useState([]);
	const [activeTaxList, setActiveTaxList] = useState([]);
	const [partyDropdownList, setPartyDropdownList] = useState([]);
	const [isQbEnabled, setIsQbEnabled] = useState(false);
	const [productServices, setProductServices] = useState([]);
	const [chartOfAccounts, setChartOfAccounts] = useState([]);
	const [classes, setClasses] = useState([]);
	const [qbDropDownLoader, setQbDropDownLoader] = useState({ productService: false, chartOfAccount: false, class: false });
	const dynamicBankOptions = invoice?.from?.id ? owner.find(o => o.id === invoice.from.id)?.banks?.length ? owner.find(o => o.id === invoice.from.id).banks : banks : banks;
	const { leaseBasicDetails } = useSelector((state) => state.leases)
	const lessorId = leaseBasicDetails?.lessor?.id || invoice?.from?.id;
	const showQbFields = isQbEnabled && hanwahaInstance && !QB_FIELD_EXCLUDED_TYPES.includes(type);

	const invoiceTypeOptions = [
		{ label: 'Rental', value: 2 },
		{ label: 'Reserve', value: 1 },
	]

	useEffect(() => {
		if (!lessorId) return;
		setDropDownLoader(l => ({ ...l, banks: true }));
		dispatch(getBanksAc({ lessor_id: lessorId, }))
			.then(bankRecords => {
				if (bankRecords?.length === 0) {
					enqueueSnackbar('No bank linked to this lessor.', {
						variant: 'error',
						anchorOrigin: { horizontal: 'right', vertical: 'top' },
					});
				}
			})
			.finally(() => setDropDownLoader(l => ({ ...l, banks: false })));
	}, [lessorId]);

	useEffect(() => {
		if (QB_FIELD_EXCLUDED_TYPES.includes(type)) return;
		globalGetService(getQbStatusApi()).then(response => {
			const enabled = Boolean(checkApiStatus(response) && response.data.data?.is_lessor_enabled);
			setIsQbEnabled(enabled);
			onQbStatusLoad?.(enabled);
		});
	}, [type]);

	useEffect(() => {
		if (!isQbEnabled) return;
		setQbDropDownLoader(l => ({ ...l, productService: true }));
		globalGetService(getProductServiceDropdownApi(lessorId)).then(psResponse => {
			setQbDropDownLoader(l => ({ ...l, productService: false }));
			if (checkApiStatus(psResponse)) {
				setProductServices(psResponse.data.data);
			}
		});
		setQbDropDownLoader(l => ({ ...l, chartOfAccount: true }));
		globalGetService(getChartOfAccountDropdownApi(lessorId)).then(coaResponse => {
			setQbDropDownLoader(l => ({ ...l, chartOfAccount: false }));
			if (checkApiStatus(coaResponse)) {
				setChartOfAccounts(coaResponse.data.data);
			}
		});
		if (hanwahaInstance) {
			setQbDropDownLoader(l => ({ ...l, class: true }));
			globalGetService(getClassesDropdownApi(lessorId)).then(classResponse => {
				setQbDropDownLoader(l => ({ ...l, class: false }));
				if (checkApiStatus(classResponse)) {
					setClasses(classResponse.data.data);
				}
			});
		}
	}, [isQbEnabled, lessorId]);

	useEffect(() => {
		if (!lessee?.length) {
			setDropDownLoader(l => ({ ...l, lessee: true }));
			dispatch(getLesseeAc({}));
		}

		if (!lessor?.length) {
			dispatch(getLessorAc({}));
		}

		if (!assetList?.length) {
			setDropDownLoader(l => ({ ...l, asset: true }));
			getAssetList();
		}

		if (!currencies?.length) {
			setDropDownLoader(l => ({ ...l, currencies: true }));
			dispatch(getCurrencyAc({}));
		}

		if (!owner?.length) {
			setDropDownLoader(l => ({ ...l, owner: true }));
			dispatch(getOwnerListApi({}));
		}

		if (!partyDropdownList?.length && leaseSlug) {
			setDropDownLoader(l => ({ ...l, party: true }));
			getPartyDropdownList();
		}
		if (!storageDetails?.remaining_storage) {
			setDropDownLoader({ ...dropDownloader, storage: true })
			dispatch(getStorage({ lease_slug: invoice?.lease_slug, module_name: "invoices", setIsDropDownLoading: setDropDownLoader }));
		}
		if (leaseSlug && type !== 'reconcileInvoice') {
			getActiveTax()
		}

	}, []);

	useEffect(() => {

		if (banks?.length) setDropDownLoader(l => ({ ...l, banks: false })); // handled via promise for empty results
		if (lessee?.length) setDropDownLoader(l => ({ ...l, lessee: false }));
		// if (lessor?.length) setDropDownLoader(l => ({ ...l, lessor: false }));
		if (assetList?.length) setDropDownLoader(l => ({ ...l, asset: false }));
		if (currencies?.length) setDropDownLoader(l => ({ ...l, currencies: false }));
		if (owner?.length) setDropDownLoader(l => ({ ...l, owner: false }));
		if (partyDropdownList?.length) setDropDownLoader(l => ({ ...l, party: false }));

		if (cleared) {
			const timeout = setTimeout(() => {
				setCleared(false);
			}, 1500);

			return () => clearTimeout(timeout);
		}

	}, [banks, lessee, lessor, assetList, currencies, owner, cleared]);

	const getAssetList = () => {
		globalGetService(getAssetListApi({ is_invoice: true })).then(response => {
			if (checkApiStatus) {
				setAssetList(response?.data?.data)
				setDropDownLoader(l => ({ ...l, asset: false }));
			}
		})
	}

	const getPartyDropdownList = () => {
		globalGetService(partyDropdownApi(leaseSlug)).then(response => {
			if (checkApiStatus) {
				const data = response?.data?.data || [];
				setPartyDropdownList(data);
				setDropDownLoader(l => ({ ...l, party: false }));
				const trustee = data.find(item => item.is_trustee === true);
				if (trustee) {
					onFieldChangeRef.current('trustee_id', trustee);
				}
			}
		})
	}
	const getActiveTax = () => {
		const activeTaxParams = type === 'linkedInvoice'
			? { parent_invoice_slug: invoice?.parent_invoice_slug }
			: { invoice_type: invoice?.invoice_type?.value || invoice?.invoice_type || 0 };
		globalGetParamsService(`lease/${leaseSlug}/additional-tax/active`, activeTaxParams)
			.then(response => {
				if (checkApiStatus(response)) {
					setActiveTaxList(response.data.data)
					onActiveTaxLoad?.(response.data.data)
				}
			})
	}

	const getInvoiceRateItems = (invoiceTypeValue) => {
		setRateItemsLoading(true);
		globalGetService(getInvoiceRateItemsApi({ lease_slug: leaseSlug, invoice_type: invoiceTypeValue }))
			.then(response => {
				setRateItemsLoading(false);
				if (checkApiStatus(response)) {
					const items = response?.data?.data?.items || [];
					setRateItems(items);
					if (!items.length) {
						const parentInvoiceType = response?.data?.data?.parent_invoice_type;
						const noRateItemsMessage = parentInvoiceType === 2 ? "Rental rate not found." : parentInvoiceType === 1 ? "Reserve rate not found." : "Rate items not found.";
						enqueueSnackbar(noRateItemsMessage, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } });
					}
				} else {
					setRateItems([]);
					enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } });
				}
			});
	}

	return (
		<>
			<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
				<TextField
					id="invoice_number"
					label="Invoice Number"
					variant="outlined"
					size='small'
					required
					value={invoice?.invoice_number || ''}
					onChange={(e) => { regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange('invoice_number', e.target.value.trimStart()) : e.preventDefault() }}
					onFocus={() => onRestErrorKey("invoice_number")}
					margin='normal'
					fullWidth
					slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
					error={error.invoice_number}
					helperText={error.invoice_number}
				/>
			</Grid>
			{["rentalInvoice", "reserveInvoice", "fleetInvoice", "linkedInvoice", "penaltyInvoice", "adhocInvoice", "securityDepositInvoice", "reconcileInvoice", "LeaseCreditDebitNote"].includes(type) ?
				<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
					<LocalizationProvider dateAdapter={AdapterMoment}>
						<DatePicker
							label="Period Start Date"
							value={invoice?.start_date ? moment(invoice.start_date) : null}
							disabled={["rentalInvoice", "reserveInvoice", "linkedInvoice", "penaltyInvoice", "securityDepositInvoice", "reconcileInvoice"].includes(type)}
							maxDate={moment()}
							onChange={(data) => {
								const isValid = moment(data)?.isValid();
								onFieldChange("start_date", data ? moment(data).format(backendDateFormat) : null);
								if (isValid) {
									onRestErrorKey("start_date");
								}
							}}
							slotProps={{
								textField: {
									required: true,
									InputLabelProps: { shrink: true },
									size: "small",
									margin: "normal",
									error: error.start_date || false,
									helperText: error.start_date || '',
									onFocus: () => {
										if (error.start_date) {
											onRestErrorKey("start_date");
										}
									}
								},
								field: {
									clearable: true,
									onClear: () => {
										onFieldChange("start_date", null);
										onRestErrorKey("start_date");
										setCleared(true);
									}
								}
							}}
							sx={{ width: "100%" }}
							format={fieldDateFormat}
						/>
					</LocalizationProvider>
				</Grid> : null}
			{["rentalInvoice", "reserveInvoice", "fleetInvoice", "linkedInvoice", "penaltyInvoice", "adhocInvoice", "securityDepositInvoice", "reconcileInvoice", "LeaseCreditDebitNote"].includes(type) ?
				<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
					<LocalizationProvider dateAdapter={AdapterMoment}>
						<DatePicker
							label="Period End Date"
							value={invoice?.end_date ? moment(invoice.end_date) : null}
							disabled={["rentalInvoice", "reserveInvoice", "linkedInvoice", "penaltyInvoice", "securityDepositInvoice", "reconcileInvoice"].includes(type)}
							onChange={(data) => {
								const isValid = moment(data)?.isValid();
								onFieldChange("end_date", data ? moment(data).format(backendDateFormat) : null);
								if (isValid) {
									onRestErrorKey("end_date");
								}
							}}
							slotProps={{
								textField: {
									required: true,
									InputLabelProps: { shrink: true },
									size: "small",
									margin: "normal",
									error: error.end_date || false,
									helperText: error.end_date || '',
									onFocus: () => {
										if (error.end_date) {
											onRestErrorKey("end_date");
										}
									}
								},
								field: {
									clearable: true,
									onClear: () => {
										onFieldChange("end_date", null);
										onRestErrorKey("end_date");
										setCleared(true);
									}
								}
							}}
							sx={{ width: "100%" }}
							format={fieldDateFormat}
						/>
					</LocalizationProvider>
				</Grid> : null}
			<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
				<LocalizationProvider dateAdapter={AdapterMoment}>
					<DatePicker
						label="Invoice Date"
						value={invoice?.date ? moment(invoice.date) : null}
						minDate={type === 'linkedInvoice' ? moment(invoice?.date_validation) : null}
						maxDate={
							(leaseBasicDetails?.end_date || invoice?.lease_end_date)
								? moment(leaseBasicDetails?.end_date || invoice?.lease_end_date).add(3, 'months')
								: null
						}
						onChange={(data) => {
							const isValid = moment(data)?.isValid();
							onFieldChange("date", data ? moment(data).format(backendDateFormat) : null);
							if (isValid) {
								onRestErrorKey("date");
							}
						}}
						slotProps={{
							textField: {
								required: "true",
								InputLabelProps: { shrink: true },
								size: "small",
								margin: "normal",
								error: error.date || false,
								helperText: error.date || '',
								onFocus: () => {
									if (error.date) {
										onRestErrorKey("date");
									}
								}
							},
							field: {
								clearable: true,
								onClear: () => {
									onFieldChange("date", null);
									onRestErrorKey("date");
									setCleared(true);
								}
							},
						}}
						sx={{ width: "100%" }}
						format={fieldDateFormat}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
				<LocalizationProvider dateAdapter={AdapterMoment}>
					<DatePicker
						label="Due Date"
						value={invoice?.due_date ? moment(invoice.due_date) : null}
						minDate={moment(invoice?.date)}
						maxDate={
							(leaseBasicDetails?.end_date || invoice?.lease_end_date)
								? moment(leaseBasicDetails?.end_date || invoice?.lease_end_date).add(3, 'months')
								: null
						}
						onChange={(data) => {
							const isValid = moment(data)?.isValid();
							onFieldChange("due_date", data ? moment(data).format(backendDateFormat) : null);
							if (isValid) {
								onRestErrorKey("due_date");
							}
						}}
						slotProps={{
							textField: {
								required: true,
								InputLabelProps: { shrink: true },
								size: "small",
								margin: "normal",
								error: error.due_date || false,
								helperText: error.due_date || '',
								onFocus: () => {
									if (error.due_date) {
										onRestErrorKey("due_date");
									}
								}
							},
							field: {
								clearable: true,
								onClear: () => {
									onFieldChange("due_date", null);
									onRestErrorKey("due_date");
									setCleared(true);
								}
							},
						}}
						sx={{ width: "100%" }}
						format={fieldDateFormat}
					/>
				</LocalizationProvider>
			</Grid>
			{["adhocInvoice", "LeaseCreditDebitNote"].includes(type) ?
				<Grid size={{ md: 4, xs: 12 }}>
					<TextField value={invoice?.asset?.name || null} size='small' label="Asset" variant="outlined" required fullWidth disabled slotProps={{ inputLabel: { shrink: true } }} />
				</Grid> : null}
			{type === 'fleetInvoice' ?
				<Grid size={{ md: 4, xs: 12 }}>
					<Autocomplete
						disablePortal
						options={assetList}
						dropDownloader={dropDownloader.asset}
						disabled={dropDownloader.asset}
						getOptionLabel={(option) => option.name || ""}
						onFocus={() => onRestErrorKey("asset")}
						onChange={(e, newValue) => {
							onFieldChange('asset', newValue);
							setFromLinkedWithAsset(false);
							setToLinkedWithAsset(false);
							if (newValue) {
								const assetLessor = newValue.owners?.[0] || null;
								const assetLessee = newValue.lessees?.[0] || null;

								if (assetLessor) {
									setFromLinkedWithAsset(true);
									onRestErrorKey('from');
								}
								if (assetLessee) {
									setToLinkedWithAsset(true);
									onRestErrorKey('to');
								}
							}
						}}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={(params) => {
							const selectedLabel = params?.inputProps?.value || "";
							return (
								<Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
									<TextField
										{...params}
										label="Asset"
										margin='normal'
										size='small'
										slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{dropDownloader?.asset ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }}
									/>
								</Tooltip>)
						}}
					/>
				</Grid> : null}
			{invoice?.parent_id || ["rentalInvoice", "reserveInvoice", 'reconcileInvoice', 'securityDepositInvoice'].includes(type) ?
				<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
					<TextField value={invoice?.currency?.name || null} size='small' label="Currency" variant="outlined" required fullWidth disabled slotProps={{ inputLabel: { shrink: true } }} />
				</Grid> :
				<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
					{(() => {
						let optionValue = currencies?.length && invoice?.currency ? currencies.find(currencyItem => currencyItem.code === invoice?.currency) : null
						return (
							<Autocomplete
								disablePortal
								disableClearable
								options={currencies}
								value={optionValue}
								dropDownloader={dropDownloader.currencies}
								disabled={dropDownloader.currencies}
								getOptionLabel={(option) => option?.name || ""}
								onFocus={() => onRestErrorKey("currency")}
								onChange={(e, newValue) => onFieldChange('currency', newValue?.code ? newValue?.code : null)}
								isOptionEqualToValue={(option, value) => option.code === value.code}
								renderInput={(params) => {
									const selectedLabel = params?.inputProps?.value || "";
									return (
										<Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
											<TextField
												required={!invoice?.parent_id}
												{...params}
												label="Currency"
												margin='normal'
												size='small'
												error={Boolean(error.currency)}
												helperText={error.currency}
												slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{dropDownloader?.currencies ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }}
											/>
										</Tooltip>)
								}}
							/>
						)
					})()}
				</Grid>}
			<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
				{invoice?.parent_id || ["rentalInvoice", "reserveInvoice", 'reconcileInvoice', 'adhocInvoice', 'securityDepositInvoice', 'LeaseCreditDebitNote'].includes(type) ?
					<TextField value={invoice?.from?.name || null} size='small' label="FROM (LESSOR/HOLDCO)" variant="outlined" fullWidth disabled slotProps={{ inputLabel: { shrink: true }, input: { endAdornment: invoiceNumberLoading ? <CircularProgress color="inherit" size={20} /> : null } }} />
					:
					<Autocomplete
						disablePortal
						disableClearable
						options={owner}
						value={invoice?.from || null}
						dropDownloader={dropDownloader.owner}
						getOptionLabel={(option) => option.name || ""}
						onFocus={() => onRestErrorKey("from")}
						disabled={fromLinkedWithAsset || dropDownloader.owner}
						onChange={(e, newValue) => onFieldChange('from', newValue)}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={(params) => {
							const selectedLabel = params?.inputProps?.value || "";
							return (
								<Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
									<TextField
										required={!invoice?.parent_id}
										{...params}
										label="FROM (LESSOR/HOLDCO)"
										margin='normal'
										size='small'
										slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{dropDownloader?.owner ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }}
										error={Boolean(error.from)}
										helperText={error.from}
									/>
								</Tooltip>)
						}}
					/>}
			</Grid>
			<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
				{invoice?.parent_id || ["rentalInvoice", "reserveInvoice", 'reconcileInvoice', 'adhocInvoice', 'securityDepositInvoice', 'LeaseCreditDebitNote'].includes(type) ?
					<TextField value={invoice?.to?.name || null} size='small' label="TO (LESSEE)" variant="outlined" fullWidth disabled slotProps={{ inputLabel: { shrink: true }, input: { endAdornment: invoiceNumberLoading ? <CircularProgress color="inherit" size={20} /> : null } }} />
					:
					<Autocomplete
						disablePortal
						disableClearable
						options={lessee}
						value={invoice?.to || null}
						dropDownloader={dropDownloader.lessee}
						getOptionLabel={(option) => option.name || ""}
						onFocus={() => onRestErrorKey("to")}
						disabled={toLinkedWithAsset || dropDownloader.lessee}
						onChange={(e, newValue) => onFieldChange('to', newValue)}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={(params) => {
							const selectedLabel = params?.inputProps?.value || "";
							return (
								<Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
									<TextField
										required={!invoice?.parent_id}
										{...params}
										label="TO (LESSEE)"
										margin='normal'
										size='small'
										error={Boolean(error.to)}
										helperText={error.to}
										slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{dropDownloader?.lessee ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }}
									/>
								</Tooltip>)
						}}
					/>}
			</Grid>
			<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
				{invoice?.parent_id || type === 'reserveInvoice' && !isBankEditable || type === 'rentalInvoice' && !isBankEditable ?
					<TextField value={invoice?.bank?.name ? invoice?.bank?.name : invoice?.bank?.account_name || null} size='small' label="Bank" required variant="outlined" fullWidth disabled slotProps={{ inputLabel: { shrink: true } }} error={error.bank} helperText={error.bank} />
					:
					<Autocomplete
						disablePortal
						disableClearable
						options={dynamicBankOptions}
						value={invoice?.bank || null}
						dropDownloader={dropDownloader.banks}
						disabled={dropDownloader.banks}
						getOptionLabel={(option) => option?.account_number ? `${option.name} (${option.account_number})` : option?.name || ""}
						onFocus={() => onRestErrorKey("bank")}
						onChange={(e, newValue) => { setIsBankEditable(true); onFieldChange("bank", newValue); }}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderOption={(props, option) => (<li {...props}> {option.name} {option.account_number && (<span style={{ fontSize: '0.75em', marginLeft: 4, color: 'inherit' }}> ({option.account_number}) </span>)} </li>)}
						renderInput={(params) => {
							const selectedLabel = params?.inputProps?.value || "";
							return (
								<Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
									<TextField
										required={!invoice?.parent_id}
										{...params}
										label="Bank"
										margin='normal'
										size='small'
										slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{dropDownloader?.banks ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }}
										error={error.bank}
										helperText={error.bank}
									/>
								</Tooltip>)
						}}
					/>}
			</Grid>
			{type === 'LeaseCreditDebitNote' ?
				<Grid size={{ md: 4, xs: 12 }}>
					<Autocomplete
						disablePortal
						disableClearable
						options={invoiceTypeOptions}
						getOptionLabel={opt => opt.label || ''}
						value={invoice.linked_invoice_type}
						onFocus={() => onRestErrorKey("linked_invoice_type")}
						onChange={(e, newValue) => {
							onAccountTypeChange(newValue);
							setRateItems([]);
							if (newValue?.value) {
								getInvoiceRateItems(newValue.value);
							}
						}}
						renderInput={params => (
							<TextField {...params}
								label="Account Type"
								required size="small"
								error={Boolean(error.linked_invoice_type)}
								helperText={error.linked_invoice_type}
								slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{rateItemsLoading ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }}
							/>
						)}
					/>
				</Grid> : null}
			{["rentalInvoice", "reserveInvoice", 'adhocInvoice', 'securityDepositInvoice', 'LeaseCreditDebitNote'].includes(type) ?
				<Grid size={{ md: ["rentalInvoice", "reserveInvoice"].includes(type) ? 3 : 4, xs: 12 }}>
					<Autocomplete
						disablePortal
						options={partyDropdownList}
						value={invoice?.trustee_id || null}
						loading={dropDownloader.party}
						disabled={dropDownloader.party}
						getOptionLabel={(option) => option.name || ""}
						onFocus={() => onRestErrorKey("trustee_id")}
						onChange={(e, newValue) => onFieldChange('trustee_id', newValue)}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={(params) => {
							const selectedLabel = params?.inputProps?.value || "";
							return (
								<Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow>
									<TextField
										{...params}
										label="Owner/Trustee"
										margin='normal'
										size='small'
										slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{dropDownloader?.party ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }}
										error={Boolean(error.trustee_id)}
										helperText={error.trustee_id}
									/>
								</Tooltip>)
						}}
					/>
				</Grid> : null}
			{/* {type === 'fleetInvoice' ?
				<Grid size={{ md: 4, xs: 12 }}>
					<Autocomplete
						disablePortal
						options={lessee}
						getOptionLabel={(option) => option.name || ""}
						onFocus={() => onRestErrorKey("invoice_division")}
						onChange={(e, newValue) => onFieldChange('invoice_division', newValue)}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Invoice Division"
								margin='normal'
								size='small'
								slotProps={{ inputLabel: { shrink: true } }}
							/>
						)}
					/>
				</Grid> : null} */}
			<AInvItems activeTaxList={activeTaxList} onFieldChange={onFieldChange} invoice={invoice} onAddRemoveInvoiceItem={onAddRemoveInvoiceItem} type={type} tableErrors={tableErrors} resetTableError={resetTableError} lgGroupedAllocation={lgGroupedAllocation} isGroupedLG={isGroupedLG} rateItems={rateItems} rateItemsLoading={rateItemsLoading} showQbFields={showQbFields} productServices={productServices} chartOfAccounts={chartOfAccounts} classes={classes} qbDropDownLoader={qbDropDownLoader} lessorId={lessorId} />
			{showPenaltyCalculation && type === 'penaltyInvoice' && !isZeroPenalty && <Grid size={{ md: 12, xs: 12 }} sx={{ mb: -2 }}><PenaltyCalculationPreview invoice={invoice} /></Grid>}
			<Grid size={{ md: 12, xs: 12 }}>
				{type === 'rentalInvoice' ? <TextField
					id="description"
					label="Description"
					variant="outlined"
					size='small'
					margin='normal'
					value={invoice?.description || ''}
					onChange={(e) => { regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("description", e.target.value.trimStart()) : e.preventDefault() }}
					slotProps={{ inputLabel: { shrink: true } }}
					sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
					fullWidth
					multiline
					rows={3}
				/> : null}
			</Grid>
			<Grid size={{ md: 12, xs: 12 }}>
				<TextField
					id="remarks"
					label="Remarks"
					variant="outlined"
					size='small'
					margin='normal'
					value={invoice?.remarks ?? ""}
					onChange={(e) => { regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("remarks", e.target.value.trimStart()) : e.preventDefault() }}
					slotProps={{ inputLabel: { shrink: true } }}
					sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
					fullWidth
					multiline
					rows={3}
				/>
			</Grid>
			<Grid size={{ md: 12, xs: 12 }} sx={{ mt: 3 }}>
				<Box className="responsive-buttons">
					{invoice?.id && invoice?.attachments?.length ?
						<Button size='small' variant='outlined' color='primary' sx={{ marginBottom: '10px' }}>({addEditInvoice?.data?.attachments?.length})Attachment(s)</Button> : <Button disableRipple color='primary' variant='outlined' size='small' sx={{ marginBottom: '10px' }}>Attachments</Button>
					}
					<Button className='available-space-btn' disableRipple size='small' variant='outlined' color='primary' endIcon={dropDownloader?.storage ? <ButtonLoader size={15} /> : null}>{`Available Space : ${dropDownloader?.storage ? "" : formatBytes(storageDetails?.remaining_storage)}`}</Button>
				</Box>
				{modeType === "view" ? null : <AttachementsUpload onFileChange={handleFileChange} attachments={invoice?.attachments} remainingStorage={storageDetails?.remaining_storage} />}
			</Grid>
		</>
	)
}

export default AInvbasicDetail